import React, { useState, useEffect } from 'react';
import {
    IonButton,
    IonIcon,
    IonInput,
    IonItem,
    IonLabel,
    IonSpinner,
    IonText,
} from '@ionic/react';
import {
    cardOutline,
    checkmarkCircleOutline,
    closeCircleOutline,
    refreshOutline,
    timeOutline,
} from 'ionicons/icons';

export type ProformaStatus = 'pending' | 'paid' | 'expired' | null;

export interface StorefrontConfirmationProps {
    proformaUuid: string | null;
    status: ProformaStatus;
    isFetching: boolean;
    errorMessage?: string;
    onCheckAgain: () => void;
    checkAgainCountdown: number;
    /** Retry path for an abandoned/cancelled payment; when set, a pay-again CTA is shown. */
    payAgainUrl?: string;
    onPayAgain?: () => void;
}

// ---------------------------------------------------------------------------
// Account-claim prompt (self-contained, non-blocking, dismissible)
// ---------------------------------------------------------------------------

interface ClaimPromptState {
    step: 'idle' | 'email' | 'code' | 'success';
    email: string;
    code: string;
    password: string;
    isFetching: boolean;
    errorMessage?: string;
}

interface AccountClaimPromptProps {
    onClaimStart: (email: string) => Promise<void>;
    onClaimComplete: (email: string, code: string, password: string) => Promise<void>;
}

export const AccountClaimPrompt: React.FC<AccountClaimPromptProps> = ({
    onClaimStart,
    onClaimComplete,
}) => {
    const [state, setState] = useState<ClaimPromptState>({
        step: 'idle',
        email: '',
        code: '',
        password: '',
        isFetching: false,
    });
    const [dismissed, setDismissed] = useState(false);

    if (dismissed || state.step === 'success') {
        if (state.step === 'success') {
            return (
                <div
                    data-testid="claim-success"
                    style={{
                        marginTop: '24px',
                        padding: '16px',
                        border: '1px solid var(--ion-color-success)',
                        borderRadius: '8px',
                        textAlign: 'center',
                    }}
                >
                    <IonIcon icon={checkmarkCircleOutline} color="success" style={{ fontSize: '32px' }} />
                    <p style={{ margin: '8px 0 0', color: 'var(--ion-color-success)' }}>
                        Account created! You can now log in with your email and password.
                    </p>
                </div>
            );
        }
        return null;
    }

    const handleStartClaim = async () => {
        setState(s => ({ ...s, isFetching: true, errorMessage: undefined }));
        try {
            await onClaimStart(state.email);
            setState(s => ({ ...s, isFetching: false, step: 'code' }));
        } catch (e: any) {
            setState(s => ({
                ...s,
                isFetching: false,
                errorMessage: e?.errorMessage ?? e?.message ?? 'Failed to send verification code.',
            }));
        }
    };

    const handleCompleteClaim = async () => {
        setState(s => ({ ...s, isFetching: true, errorMessage: undefined }));
        try {
            await onClaimComplete(state.email, state.code, state.password);
            setState(s => ({ ...s, isFetching: false, step: 'success' }));
        } catch (e: any) {
            setState(s => ({
                ...s,
                isFetching: false,
                errorMessage: e?.errorMessage ?? e?.message ?? 'Failed to complete account setup.',
            }));
        }
    };

    return (
        <div
            data-testid="claim-prompt"
            style={{
                marginTop: '32px',
                padding: '16px',
                border: '1px solid var(--ion-color-light-shade)',
                borderRadius: '8px',
            }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <IonText>
                    <h3 style={{ margin: 0 }}>Save your purchase</h3>
                </IonText>
                <IonButton
                    fill="clear"
                    size="small"
                    onClick={() => setDismissed(true)}
                    data-testid="claim-dismiss"
                >
                    Dismiss
                </IonButton>
            </div>

            <p style={{ color: 'var(--ion-color-medium)', fontSize: '0.9rem', margin: '4px 0 12px' }}>
                Create an account to view your order history and manage bookings.
            </p>

            {state.step === 'idle' && (
                <>
                    <IonItem lines="none" style={{ '--padding-start': '0', '--inner-padding-end': '0' }}>
                        <IonLabel position="stacked">Email address</IonLabel>
                        <IonInput
                            type="email"
                            value={state.email}
                            placeholder="you@example.com"
                            onIonInput={(e) => setState(s => ({ ...s, email: String(e.detail.value ?? '') }))}
                            data-testid="claim-email-input"
                        />
                    </IonItem>
                    {state.errorMessage && (
                        <IonText color="danger" data-testid="claim-error">
                            <p style={{ fontSize: '0.85rem' }}>{state.errorMessage}</p>
                        </IonText>
                    )}
                    <IonButton
                        expand="block"
                        onClick={handleStartClaim}
                        disabled={state.isFetching || !state.email}
                        data-testid="claim-start-button"
                        style={{ marginTop: '8px' }}
                    >
                        {state.isFetching ? <IonSpinner name="crescent" /> : 'Create account'}
                    </IonButton>
                </>
            )}

            {state.step === 'code' && (
                <>
                    <p style={{ fontSize: '0.9rem', margin: '0 0 12px' }}>
                        We sent a 6-digit code to <strong>{state.email}</strong>. Enter it below along with your new
                        password.
                    </p>
                    <IonItem lines="none" style={{ '--padding-start': '0', '--inner-padding-end': '0' }}>
                        <IonLabel position="stacked">Verification code</IonLabel>
                        <IonInput
                            type="text"
                            value={state.code}
                            placeholder="123456"
                            maxlength={6}
                            onIonInput={(e) => setState(s => ({ ...s, code: String(e.detail.value ?? '') }))}
                            data-testid="claim-code-input"
                        />
                    </IonItem>
                    <IonItem lines="none" style={{ '--padding-start': '0', '--inner-padding-end': '0' }}>
                        <IonLabel position="stacked">New password</IonLabel>
                        <IonInput
                            type="password"
                            value={state.password}
                            placeholder="Choose a password"
                            onIonInput={(e) => setState(s => ({ ...s, password: String(e.detail.value ?? '') }))}
                            data-testid="claim-password-input"
                        />
                    </IonItem>
                    {state.errorMessage && (
                        <IonText color="danger" data-testid="claim-error">
                            <p style={{ fontSize: '0.85rem' }}>{state.errorMessage}</p>
                        </IonText>
                    )}
                    <IonButton
                        expand="block"
                        onClick={handleCompleteClaim}
                        disabled={state.isFetching || !state.code || !state.password}
                        data-testid="claim-complete-button"
                        style={{ marginTop: '8px' }}
                    >
                        {state.isFetching ? <IonSpinner name="crescent" /> : 'Confirm'}
                    </IonButton>
                </>
            )}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Main confirmation component
// ---------------------------------------------------------------------------

const RATE_LIMIT_SECONDS = 30;

export const StorefrontConfirmationComponent: React.FC<
    StorefrontConfirmationProps & {
        onClaimStart: (email: string) => Promise<void>;
        onClaimComplete: (email: string, code: string, password: string) => Promise<void>;
    }
> = ({
    proformaUuid,
    status,
    isFetching,
    errorMessage,
    onCheckAgain,
    checkAgainCountdown,
    payAgainUrl,
    onPayAgain,
    onClaimStart,
    onClaimComplete,
}) => {
    const [countdown, setCountdown] = useState(checkAgainCountdown);

    useEffect(() => {
        setCountdown(checkAgainCountdown);
    }, [checkAgainCountdown]);

    useEffect(() => {
        if (countdown > 0) {
            const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [countdown]);

    const handleCheckClick = () => {
        if (countdown === 0 && !isFetching) {
            onCheckAgain();
            setCountdown(RATE_LIMIT_SECONDS);
        }
    };

    const getIcon = () => {
        if (status === 'paid') return checkmarkCircleOutline;
        if (status === 'expired' || errorMessage) return closeCircleOutline;
        return timeOutline;
    };

    const getColor = () => {
        if (status === 'paid') return 'success';
        if (status === 'expired' || errorMessage) return 'danger';
        return 'warning';
    };

    const getTitle = () => {
        if (errorMessage) return 'Error';
        if (status === 'paid') return 'Payment Confirmed';
        if (status === 'expired') return 'Payment Expired';
        return 'Confirming your payment…';
    };

    const getDescription = () => {
        if (errorMessage) return errorMessage;
        if (status === 'paid') return 'Your order is confirmed. Thank you for your purchase!';
        if (status === 'expired')
            return 'This payment link has expired. If you believe this is an error, please contact the school.';
        return 'We are confirming your payment. This usually only takes a moment.';
    };

    if (!proformaUuid) {
        return (
            <div className="ion-padding ion-text-center">
                <IonIcon icon={closeCircleOutline} color="danger" style={{ fontSize: '64px', marginBottom: '16px' }} />
                <IonText color="danger">
                    <h2>Invalid confirmation link</h2>
                    <p>No order reference found. Please return to the shop and try again.</p>
                </IonText>
            </div>
        );
    }

    const showCheckAgain = status === 'pending' && !errorMessage;
    const showClaim = status === 'paid';

    return (
        <div className="ion-padding">
            <div className="ion-text-center" style={{ paddingTop: '24px' }}>
                <IonIcon
                    icon={getIcon()}
                    color={getColor()}
                    style={{ fontSize: '64px', marginBottom: '16px' }}
                    data-testid="confirmation-icon"
                />
                <IonText color={getColor()}>
                    <h2 data-testid="confirmation-title">{getTitle()}</h2>
                </IonText>
                <p data-testid="confirmation-description">{getDescription()}</p>

                {showCheckAgain && payAgainUrl && onPayAgain && (
                    <div className="ion-margin-top" data-testid="pay-again-section">
                        <IonButton
                            expand="block"
                            onClick={onPayAgain}
                            data-testid="pay-again-button"
                            color="primary"
                        >
                            <IonIcon icon={cardOutline} slot="start" />
                            Pay Again
                        </IonButton>
                        <p style={{ fontSize: '0.8rem', color: 'var(--ion-color-medium)' }}>
                            Didn't finish paying? Resume your payment — this won't create a duplicate order.
                        </p>
                    </div>
                )}

                {showCheckAgain && (
                    <div className="ion-margin-top" data-testid="check-again-section">
                        <IonButton
                            expand="block"
                            onClick={handleCheckClick}
                            disabled={isFetching || countdown > 0}
                            data-testid="check-again-button"
                            color="primary"
                        >
                            {isFetching ? (
                                <IonSpinner name="crescent" />
                            ) : (
                                <>
                                    <IonIcon icon={refreshOutline} slot="start" />
                                    Check Again {countdown > 0 ? `(${countdown}s)` : ''}
                                </>
                            )}
                        </IonButton>
                        {countdown > 0 && (
                            <p style={{ fontSize: '0.8rem', color: 'var(--ion-color-medium)' }}>
                                You can check again in {countdown} seconds.
                            </p>
                        )}
                    </div>
                )}
            </div>

            {showClaim && (
                <AccountClaimPrompt
                    onClaimStart={onClaimStart}
                    onClaimComplete={onClaimComplete}
                />
            )}
        </div>
    );
};

export default StorefrontConfirmationComponent;
